import { useEffect, useRef } from 'react'
import type { CSSProperties } from 'react'
import { useLocation } from 'react-router-dom'
import styles from './BrandText.module.css'

interface Props {
  text?: string
  className?: string
}

type Direction = 'b2f' | 'f2b'

/**
 * Animated wordmark that pairs with <LogoMark>.
 *
 * Each letter is its own <span> so it can hop up and back on a
 * staggered timeline. The stagger direction (b2f vs f2b) alternates
 * with every route change, matching <LogoMark>'s wave direction —
 * both components hold their own ref-based flip but the logic is
 * identical and they mount together, so they stay in sync.
 */
export default function BrandText({ text = 'DepthZone', className }: Props) {
  const location = useLocation()
  const k = location.pathname

  const directionRef = useRef<Direction>('b2f')
  useEffect(() => {
    directionRef.current = directionRef.current === 'b2f' ? 'f2b' : 'b2f'
  }, [k])

  const dirClass = directionRef.current === 'b2f' ? styles.b2f : styles.f2b
  const letters = [...text]

  return (
    <span
      className={`${styles.root} ${dirClass}${className ? ` ${className}` : ''}`}
      style={{ '--n': letters.length } as CSSProperties}
      aria-label={text}
    >
      {letters.map((char, i) => (
        <span
          key={`${i}-${k}`}
          className={styles.letter}
          style={{ '--i': i } as CSSProperties}
          aria-hidden="true"
        >
          {char}
        </span>
      ))}
    </span>
  )
}
